import { useState, useEffect, useRef, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "assets/styles/Header.css";
import logo from "assets/images/logo-white.svg";
import Hamburguer from "components/Hamburger";
import classNames from "classnames";

export default function Header() {
  const { navigation } = require("configs/settings.json");

  const [mode, setMode] = useState("natural");
  const [isVisible, setIsVisible] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const lastScrollRef = useRef(0);
  const headerRef = useRef(null);
  const isMobileRef = useRef(window.innerWidth < 768);

  // 👉 Flag para correr la intro SOLO una vez
  const didIntroRef = useRef(false);
  useEffect(() => {
    didIntroRef.current = true;
  }, []);

  const handleOnClick = () => setIsOpen((prev) => !prev);

  // Arrancar SIEMPRE en top y desactivar restauración automática del scroll
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
    const onPageShow = () => window.scrollTo(0, 0);
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Manejar resize para detectar mobile
  useEffect(() => {
    const handleResize = () => {
      isMobileRef.current = window.innerWidth < 768;
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Bloquear scroll al abrir el menú mobile
  useEffect(() => {
    document.body.classList.toggle("overflow-hidden", isOpen);
  }, [isOpen]);

  // Medir altura
  useEffect(() => {
    const setHeaderVar = () => {
      const h = headerRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty("--header-h", `${h}px`);
    };
    setHeaderVar();
    window.addEventListener("resize", setHeaderVar);
    return () => window.removeEventListener("resize", setHeaderVar);
  }, []);

  // Clase opcional para estados fixed
  useEffect(() => {
    document.body.classList.toggle("has-fixed-header", mode === "fixed");
  }, [mode]);

  // Scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;
      const last = lastScrollRef.current;
      const goingDown = current > last;
      const goingUp = current < last;
      const threshold = (headerRef.current?.offsetHeight || 96) * 1.5;

      if (current <= 0) {
        if (!isMobileRef.current) {
          setMode("natural");
          setIsVisible(true);
        }
        lastScrollRef.current = 0;
        return;
      }

      if (mode === "natural") {
        if (current > threshold) {
          setMode("fixed");
          setIsVisible(!goingDown);
        }
      } else {
        if (goingDown) setIsVisible(false);
        else if (goingUp) setIsVisible(true);
      }

      lastScrollRef.current = current;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mode]);

  // ===== Animaciones =====
  const logoVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
    }),
    []
  );

  const navListVariants = useMemo(
    () => ({
      hidden: { opacity: 1 },
      visible: {
        opacity: 1,
        transition: { staggerChildren: 0.08, delayChildren: 0.1 },
      },
    }),
    []
  );

  const navItemVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
    }),
    []
  );

  const fadeTransition = { duration: 0.28, ease: "easeOut" };

  const headerVariants = useMemo(
    () => ({
      hiddenUp: { y: "-100%", opacity: 1 },
      visible: { y: 0, opacity: 1 },
    }),
    []
  );
  const transition = { type: "spring", stiffness: 500, damping: 40 };

  const handleNavClick = () => {
    try {
      window.scrollTo(0, 0);
    } catch (_) {}
  };

  const introInitial = didIntroRef.current ? false : "hidden";
  const introAnimate = "visible";

  const HeaderTag = mode === "fixed" ? motion.header : "header";
  const isFixedOpen = mode === "fixed" && isOpen;

  return (
    <HeaderTag
      ref={headerRef}
      className={classNames("header", {
        "is-fixed": mode === "fixed",
        "is-natural": mode === "natural",
        "header--full": isFixedOpen,
      })}
      {...(mode === "fixed"
        ? {
            initial: "hiddenUp",
            animate: isVisible ? "visible" : "hiddenUp",
            variants: headerVariants,
            transition,
          }
        : {})}
      role="banner"
    >
      <div className="header-inner container">
        <NavLink
          to="/"
          className="mr-auto logo"
          reloadDocument
          onClick={handleNavClick}
        >
          <motion.img
            src={logo}
            alt="Kollectiff"
            initial={introInitial}
            animate={introAnimate}
            variants={logoVariants}
            transition={fadeTransition}
          />
        </NavLink>

        <nav aria-label="Primary">
          <motion.ul
            initial={introInitial}
            animate={introAnimate}
            variants={navListVariants}
          >
            {navigation.main.map((item) => (
              <motion.li
                key={item.link}
                variants={navItemVariants}
                transition={fadeTransition}
              >
                <NavLink
                  to={item.link}
                  reloadDocument
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    classNames("link-md", { active: isActive })
                  }
                >
                  {item.text}
                </NavLink>
              </motion.li>
            ))}
          </motion.ul>
        </nav>

        <Hamburguer active={isOpen} onClick={handleOnClick} />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.nav
            className="mobile-nav bg-black/50 backdrop-blur-md"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.2 }}
            aria-label="Mobile"
          >
            <ul>
              {navigation.main.map((item) => (
                <li key={item.link}>
                  <NavLink
                    to={item.link}
                    reloadDocument
                    onClick={() => {
                      setIsOpen(false);
                      handleNavClick();
                    }}
                    className={({ isActive }) =>
                      `${isActive ? "active" : ""} link-md`
                    }
                  >
                    {item.text}
                  </NavLink>
                </li>
              ))}
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </HeaderTag>
  );
}